import * as API from "../../API";
import { InternetConnectionError } from "../../HttpRequest";
import * as messages from "../../messages";
import { CaregiverNotificationId } from "../../messages/ObjectIds";
import { EventSource } from "../EventSource";
import { OrderedActionProcessor } from "../OrderedActionProcessor";

/**
 * The server method that is used to get the list of notifications
 */
export type NotificationRefreshFn = (api: API.API) => Promise<messages.PushNotificationInfoList>;

export class NotificationState {
  constructor(
    private readonly api: API.API,
    private readonly notificationRefreshFn: NotificationRefreshFn
  ) {
    this.notificationsDB = new NotificationsDBImpl();

    this.notificationsRefreshProcessor =
      new OrderedActionProcessor<messages.PushNotificationInfoList>(
        (payload: messages.PushNotificationInfoList) => {
          this.handleNotificationsReceived(payload);
        },
        (err: any) => {
          if (err instanceof InternetConnectionError) {
            return true;
          } else if (err instanceof API.ServerError) {
            return true;
          }
          console.warn("Unknown network error", err);
          return false;
        }
      );
    this.triggerNotificationsRefresh();
  }

  private notificationsRefreshProcessor: OrderedActionProcessor<messages.PushNotificationInfoList>;

  private handleNotificationsReceived(notifications: messages.PushNotificationInfoList): void {
    this.notificationsDB.setNotifications(notifications);
  }

  /**
   * Should be called whenever an event is received indicating a new notification.
   */
  public triggerNotificationsRefresh(): void {
    this.notificationsRefreshProcessor.enqueue(() => {
      return this.notificationRefreshFn(this.api);
    });
  }

  public addNotificationsFromRealTimePush(notificationsToAdd: messages.PushNotificationInfoList) {
    this.notificationsDB.addNotifications(notificationsToAdd.pushNotificationInfos);
    this.onNotificationsUpdated.broadcast(undefined);
  }

  setNotificationsViewed(): void {
    this.notificationsDB.setNotificationsViewed();
    this.onNotificationsUpdated.broadcast(undefined);
  }

  setNotificationClicked(notificationId: CaregiverNotificationId): void {
    this.notificationsDB.setNotificationClicked(notificationId);
    this.onNotificationsUpdated.broadcast(undefined);
  }

  public readonly onNotificationsUpdated = new EventSource<void>();

  private notificationsDB: NotificationsDBImpl;

  public getNotificationsDB(): NotificationsDB {
    return this.notificationsDB;
  }

  private closed = false;
  public close(): void {
    if (this.closed) {
      throw new Error("AppState already closed");
    }
    this.closed = true;

    this.notificationsRefreshProcessor.close();
  }
}

export abstract class NotificationsDB {
  public abstract getNotifications(): messages.PushNotificationInfoList;
  public abstract getNumUnviewedNotifications(): number;
}

class NotificationsDBImpl implements NotificationsDB {
  private notifications: messages.PushNotificationInfoList = {
    pushNotificationInfos: [],
  };

  public setNotifications(notifications: messages.PushNotificationInfoList) {
    this.notifications = notifications;
  }

  public getNotifications(): messages.PushNotificationInfoList {
    return this.notifications;
  }

  public getNumUnviewedNotifications(): number {
    let counter = 0;
    for (const notification of this.notifications.pushNotificationInfos) {
      if (!notification.viewed) {
        counter++;
      }
    }
    return counter;
  }

  setNotificationsViewed(): void {
    this.notifications.pushNotificationInfos.forEach((notification) => {
      notification.viewed = true;
    });
  }

  setNotificationClicked(notificationId: CaregiverNotificationId): void {
    for (const notification of this.notifications.pushNotificationInfos) {
      if (notification.id === notificationId) {
        notification.clicked = true;
        notification.viewed = true;
        break;
      }
    }
  }

  addNotifications(notificationsToAdd: messages.PushNotificationInfo[]) {
    this.notifications.pushNotificationInfos = notificationsToAdd.concat(
      this.notifications.pushNotificationInfos
    );
  }
}
